// ------------------------------------
// Constants
// ------------------------------------
import request from 'utils/request'
import { getUTCOffset } from 'utils/time'
import { modalActions } from 'store/modal'
import { LOCAL_STORAGE_KEYS, SESSION_STORAGE_KEYS, HPI_HARDWARE_ID_HEADER_NAME } from 'utils/constants'
import { actions as loginActions } from 'routes/Login/modules/Login'
import { removeConsentClosedFromLocalStorage } from '../routes/Consent/utils/consentUtils'
import { onFetchConsent } from '../routes/Consent/modules/Consent'
import { caregiverActions } from './selectedParticipant'
import { getHpiHardwareIdHeaderValue } from 'utils/hardwareId'
import localizeApp from 'utils/localization'
import {
  logSentryEvent,
  setSentryUser,
  clearSentryUser,
  SENTRY_MESSAGES,
} from 'utils/sentry'
import packageJson from '../../package.json'

export const USER_LOGGED_IN = 'USER_LOGGED_IN'
export const USER_LOGGED_OUT = 'USER_LOGGED_OUT'
export const USER_CONSENTED = 'USER_CONSENTED'
export const USER_CHANGED_PASSWORD = 'USER_CHANGED_PASSWORD'
export const UPDATE_USERNAME = 'UPDATE_USERNAME'
export const SET_USER_IMAGE = 'SET_USER_IMAGE'
export const SET_USER_DATA = 'SET_USER_DATA'

function login(username, password) {
  return (dispatch, getState) => {
    const hpiHardwareId = getHpiHardwareIdHeaderValue(username)
    const { strings } = getState()
    const success = jsonBody => {
      const payload = Object.assign(
        JSON.parse(atob(jsonBody.token.split('.')[1])),
        jsonBody
      )
      const userData = JSON.stringify(payload)
      localStorage.setItem(LOCAL_STORAGE_KEYS.user, userData)
      setSentryUser(payload)
      logSentryEvent({
        message: SENTRY_MESSAGES.signIn,
        tags: { flow: 'auth', action: 'sign_in' },
      })
      dispatch({
        type: USER_LOGGED_IN,
        payload
      })
      localizeApp({ dispatch, getState })
      dispatch(loginActions.onAppOpened())
      dispatch(loginActions.clearLoginError())
      if (payload.is_temporary_password) {
        dispatch(loginActions.setTempPassword(password))
      }
      // Skip consent flow for caregivers
      if (!payload.is_caregiver) {
        dispatch(onFetchConsent())
      } else {
        // For caregivers, fetch participants immediately after login
        // If there's only one participant, it will be auto-selected automatically
        dispatch(caregiverActions.fetchParticipants())
      }
    }
    return dispatch(
      request({
        method: 'POST',
        url: '/control/login',
        body: JSON.stringify({ username, password }),
        authRequired: false,
        headerOptions: {
          'hpi-device-type': 'web',
          'hpi-utc-offset': getUTCOffset(),
          'hpi-app-version': packageJson.version,
          [HPI_HARDWARE_ID_HEADER_NAME]: hpiHardwareId,

        },
        success,
        fail: (response, message) => {
          logSentryEvent({
            message: SENTRY_MESSAGES.loginFailed,
            level: 'error',
            tags: {
              flow: 'auth',
              action: 'login_failed',
              'http.status': response?.status,
            },
          })
          return loginActions.setLoginError(response, message, strings)
        }
      })
    )
  }
}

const clearLocalSession = dispatch => {
  clearSentryUser()
  removeConsentClosedFromLocalStorage()

  localStorage.removeItem(LOCAL_STORAGE_KEYS.user)
  // Clear participant-specific data from localStorage
  try {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.selectedPtp)
  } catch (_) { }
  // Clear sessionStorage for caregiver participants data
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.caregiverParticipants)
  } catch (_) { }

  dispatch(loginActions.setLoginError(''))
  dispatch({
    type: USER_LOGGED_OUT
  })
  dispatch(modalActions.closeModal())
}

/** Clear local session only (no server call). Used when session is already invalid (401, expiry). */
function clearSession() {
  return dispatch => {
    clearLocalSession(dispatch)
  }
}

/** User-initiated logout: notify server, then clear local session. */
function logout() {
  return dispatch => {
    logSentryEvent({
      message: SENTRY_MESSAGES.signOut,
      tags: { flow: 'auth', action: 'sign_out' },
    })
    return Promise.resolve(
      dispatch(
        request({
          method: 'POST',
          url: '/control/logout',
          authRequired: false,
          suppressLogoutOnAuthError: true,
          skipNotyFail: true,
          skipNetworkErrorReporting: true,
          headerOptions: {
            'hpi-device-type': 'web',
            'hpi-utc-offset': getUTCOffset(),
            'hpi-app-version': packageJson.version,
          },
          success: () => true,
          fail: () => true,
        }),
      ),
    ).finally(() => clearLocalSession(dispatch))
  }
}

function updateUsername(username) {
  return {
    type: UPDATE_USERNAME,
    payload: username
  }
}

function changedPassword() {
  return {
    type: USER_CHANGED_PASSWORD
  }
}

function setUserData(userData) {
  return {
    type: SET_USER_DATA,
    payload: userData
  }
}

export function fetchUserImage(userID) {
  return dispatch => {
    const errorMessage = 'Could not fetch user image'
    return dispatch(
      request({
        method: 'GET',
        url: `/control/assets/profile_pics/${userID}`,
        headerOptions: { 'Content-Type': 'image' },
        errorMessage,
        success: json => {
          dispatch({
            type: SET_USER_IMAGE,
            payload: json.asset
          })
        },
        fail: () => { } // console.log('user does not have an image'),
      })
    )
  }
}

export default function userUpdate(state, { type, payload } = {}) {
  const currentState = state === undefined
    ? JSON.parse(localStorage.getItem('user')) || {}
    : state
  let newState
  switch (type) {
    case UPDATE_USERNAME:
      return { ...currentState, name: payload }
    case USER_LOGGED_IN:
      return payload
    case SET_USER_DATA:
      return {
        ...currentState,
        ...payload
      }
    case USER_LOGGED_OUT:
      return {}
    case USER_CONSENTED:
      newState = { ...currentState, has_consented: true }
      localStorage.setItem(LOCAL_STORAGE_KEYS.user, JSON.stringify(newState))
      return newState
    case USER_CHANGED_PASSWORD:
      newState = { ...currentState, is_temporary_password: false }
      localStorage.setItem(LOCAL_STORAGE_KEYS.user, JSON.stringify(newState))
      return newState
    case SET_USER_IMAGE:
      return { ...currentState, userImage: payload }
    default:
      return currentState
  }
}

export const authActions = {
  login,
  logout,
  clearSession,
  updateUsername,
  changedPassword,
  setUserData
}
